import { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import Chrome from '../../i18n/Chrome';

/**
 * Renders children only after `delayMs` — skips the flash when work finishes quickly.
 * Use for Suspense fallbacks and page data loaders.
 */
export function DelayedShow({ delayMs = 350, children, placeholder = null }) {
  const [show, setShow] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return undefined;
    let active = true;
    const t = setTimeout(() => {
      if (active) setShow(true);
    }, delayMs);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [delayMs]);

  if (!show) return placeholder;
  return children;
}

/** Open-book outline — no card chrome */
function BookMark({ className }) {
  return (
    <svg
      className={cn('sw-loader__book', className)}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        className="sw-loader__book-cover"
        d="M8 10c0-1.5 1.2-2.5 2.8-2.5H22v33H10.8C9.2 40.5 8 39.5 8 38V10z"
        fill="currentColor"
        fillOpacity="0.08"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        className="sw-loader__book-page"
        d="M40 10c0-1.5-1.2-2.5-2.8-2.5H26v33h11.2c1.6 0 2.8-1 2.8-2.5V10z"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M24 7.5v33"
        stroke="#F7B731"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        className="sw-loader__book-lines"
        d="M12 16h8M12 21h8M12 26h6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.4"
      />
      <path
        className="sw-loader__book-lines sw-loader__book-lines--right"
        d="M28 16h8M28 21h8M28 26h6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.3"
      />
    </svg>
  );
}

function LoaderStage() {
  return (
    <div className="sw-loader__stage" aria-hidden>
      <span className="sw-loader__orbit sw-loader__orbit--outer" />
      <span className="sw-loader__orbit sw-loader__orbit--mid" />
      <span className="sw-loader__orbit sw-loader__orbit--inner" />
      <span className="sw-loader__sweep" />
      <span className="sw-loader__node sw-loader__node--a" />
      <span className="sw-loader__node sw-loader__node--b" />
      <span className="sw-loader__node sw-loader__node--c" />
      <div className="sw-loader__mark">
        <BookMark />
      </div>
    </div>
  );
}

function LoaderCopy({ label, showBrand, showTagline, showDots }) {
  return (
    <div className="sw-loader__copy">
      {showBrand ? (
        <p className="sw-loader__brand">
          Swotify <span>Plus</span>
        </p>
      ) : null}
      <LoaderStage />
      {label ? (
        <p className="sw-loader__label">
          <Chrome text={label} />
        </p>
      ) : null}
      {showDots ? (
        <span className="sw-loader__dots" aria-hidden>
          <i /><i /><i />
        </span>
      ) : null}
      {showTagline ? (
        <p className="sw-loader__tagline">Intelligence · Growth · Innovation</p>
      ) : null}
    </div>
  );
}

/**
 * Branded Swotify Plus loader.
 * Prefer DelayedShow / PageLoader / FullscreenLoader so fast loads never flash.
 */
export default function SwotifyLoader({
  variant = 'page',
  label,
  className,
  dark = false,
  showBrand,
}) {
  const statusLabel = label
    ?? (variant === 'route' || variant === 'overlay'
      ? 'Loading page…'
      : variant === 'fullscreen'
        ? 'Preparing your workspace…'
        : 'Loading…');

  const brand =
    showBrand ?? (variant === 'fullscreen');

  if (variant === 'inline') {
    return (
      <span
        className={cn('sw-loader sw-loader--inline', className)}
        role="status"
        aria-live="polite"
        aria-label={statusLabel}
      >
        <span className="sw-loader__inline-ring" aria-hidden />
        {label ? (
          <span className="sw-loader__inline-label">
            <Chrome text={label} />
          </span>
        ) : null}
      </span>
    );
  }

  const copy = (
    <LoaderCopy
      label={statusLabel}
      showBrand={brand}
      showTagline={variant === 'fullscreen'}
      showDots={variant !== 'compact'}
    />
  );

  if (variant === 'fullscreen') {
    return (
      <div
        className={cn('sw-loader sw-loader--fullscreen', dark && 'sw-loader--dark', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={statusLabel}
      >
        <div className="sw-loader__fullscreen-grid" aria-hidden />
        <div className="sw-loader__fullscreen-inner">{copy}</div>
      </div>
    );
  }

  if (variant === 'route') {
    return (
      <div
        className={cn('sw-loader sw-loader--route', dark && 'sw-loader--dark', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={statusLabel}
      >
        <div className="sw-loader__route-scrim" aria-hidden />
        <div className="sw-loader__route-panel">{copy}</div>
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      <div
        className={cn('sw-loader sw-loader--overlay', dark && 'sw-loader--dark', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={statusLabel}
      >
        <div className="sw-loader__overlay-scrim" aria-hidden />
        <div className="sw-loader__overlay-panel">{copy}</div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={cn('sw-loader sw-loader--compact', dark && 'sw-loader--dark', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={statusLabel}
      >
        <LoaderStage />
        {label ? (
          <p className="sw-loader__label sw-loader__label--compact">
            <Chrome text={label} />
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn('sw-loader sw-loader--page', dark && 'sw-loader--dark', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={statusLabel}
    >
      {copy}
    </div>
  );
}

/** How long to wait before showing a loader (skip for fast loads). */
export const LOADER_SHOW_DELAY_MS = 400;

/** Viewport boot / Suspense fallback — delayed so fast chunk loads never flash. */
export function FullscreenLoader({ delayMs = LOADER_SHOW_DELAY_MS, ...props }) {
  return (
    <DelayedShow delayMs={delayMs}>
      <SwotifyLoader variant="fullscreen" showBrand {...props} />
    </DelayedShow>
  );
}

/** In-page loader — delayed so quick fetches never flash. */
export function PageLoader({
  label = 'Loading…',
  className,
  minHeight = true,
  tall = false,
  delayMs = LOADER_SHOW_DELAY_MS,
}) {
  return (
    <DelayedShow
      delayMs={delayMs}
      placeholder={
        <div
          className={cn(
            'sw-page-loader',
            minHeight && (tall ? 'sw-page-loader--tall' : 'sw-page-loader--min'),
            className,
          )}
          aria-hidden
        />
      }
    >
      <div
        className={cn(
          'sw-page-loader',
          minHeight && (tall ? 'sw-page-loader--tall' : 'sw-page-loader--min'),
          className,
        )}
      >
        <SwotifyLoader variant="page" label={label} showBrand={false} />
      </div>
    </DelayedShow>
  );
}

export function OverlayLoader(props) {
  return <SwotifyLoader variant="overlay" {...props} />;
}

export function RouteLoader(props) {
  return <SwotifyLoader variant="route" label="Loading page…" showBrand={false} {...props} />;
}
